/**
 * Object fields as the run's bytes need them: a key dropped, or a key kept only while it says
 * something. The state hash is JSON over the state, so these keep the order of every remaining
 * key and leave an absent field absent (an older run's bytes do not move when a rule is off).
 */

/** A copy of `value` without `keys`; the remaining keys keep their order, as rest destructuring does. */
export function without<Value extends object, Key extends keyof Value>(
  value: Value,
  ...keys: readonly Key[]
): Omit<Value, Key> {
  const copy: Partial<Value> = { ...value };
  for (const key of keys) {
    delete copy[key];
  }
  return copy as Omit<Value, Key>;
}

/** `{ [key]: value }` when the value is defined, else no field: spread it into a literal. */
export function optionalField<Key extends string, Value>(
  key: Key,
  value: Value | undefined,
): Partial<Record<Key, Value>> {
  return value === undefined ? {} : ({ [key]: value } as Partial<Record<Key, Value>>);
}

/** `{ [key]: count }` while the count is above 0, else no field: a tally kept absent at nothing. */
export function positiveField<Key extends string>(
  key: Key,
  count: number,
): Partial<Record<Key, number>> {
  return count > 0 ? ({ [key]: count } as Partial<Record<Key, number>>) : {};
}
